import {Instance} from '@popperjs/core';
import React, {useMemo, useRef, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import {v4 as uuid} from 'uuid';
import {Box, ClickAwayListener, Divider, Stack, styled} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Icon,
  Tooltip,
} from '@lightricks/react-design-system';
import AssetErrorsNotificationBanner from '@contentlab/components/asset-errors-notification-banner';
import {FEATURE_BEST_PRACTICES_LOCALE_KEY} from '@contentlab/config/constants';
import {getUploadLimits} from '@contentlab/config/uploadLimits';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useProcessEvent from '@contentlab/hooks/analytics/use-process-event';
import {
  AssetErrors,
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import {FeatureName} from '@contentlab/types/project';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {onVideoUpload} from '../file-upload/utils';
import BestPracticesTooltip from './BestPracticesTooltip';

type FileUploaderProps = {
  height?: string | number;
  width?: string | number;
  maxWidth?: string | number;
  analyticsLocation: string;
  feature: FeatureName | string;
  assetErrors: AssetErrors[];
  isLoading: boolean;
  showBestPractices: boolean;
};

const shouldNotForwardProps = ['isDragActive'];

const UploaderContainer = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{
  isDragActive?: boolean;
}>(({isDragActive}) => ({
  backgroundColor: isDragActive
    ? designSystemToken('semantic.bg.tertiary')
    : designSystemToken('semantic.bg.secondary'),
  border: `1px dashed ${designSystemToken('semantic.fg.tertiary')}`,
  padding: '20px 16px 32px 16px',
  cursor: 'pointer',
  display: 'grid',
  placeItems: 'center',
  borderRadius: '24px',
}));

const DropzoneArea = styled('div')(() => ({
  display: 'flex',
  flexFlow: 'row nowrap',
  alignItems: 'center',
  width: '100%',
  height: '100%',
}));

const UploaderContentStack = styled(Stack)(() => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: '40px',
  justifyContent: 'center',
  alignItems: 'center',
}));

const TopStack = styled(Stack)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: '36px',
  gap: '12px',
}));

const BottomStack = styled(Stack)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
}));

const UploadIconContainer = styled(Box)(() => ({
  backgroundColor: designSystemToken('semantic.bg.neutral-inverse'),
  borderRadius: '50%',
  width: '48px',
  height: '48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const BestPracticesContainer = styled(Stack)(() => ({
  alignItems: 'center',
  borderRadius: '8px',
  marginY: '8px',
}));

const BestPracticesRow = styled(Stack)(() => ({
  alignItems: 'center',
  gap: '4px',
}));

const ActionButton = styled(Button)(() => ({
  width: '200px',
}));

const DividerStyled = styled(Divider)(() => ({
  width: '271px',
  alignSelf: 'center',
}));

function OnboardingFileUploader({
  height,
  width,
  maxWidth,
  analyticsLocation,
  feature,
  assetErrors,
  isLoading,
  showBestPractices = true,
}: FileUploaderProps) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const cleanedFeatureName = useMemo(
    () =>
      feature.replace(
        `${CONTENT_LAB_VIEW_NAMES.ONBOARDING_UPLOAD_MODAL}--`,
        ''
      ) as FeatureName,
    [feature]
  );
  const uploadLimits = getUploadLimits(cleanedFeatureName);
  const errorCodes = useMemo(() => {
    if (assetErrors.length) {
      return assetErrors[0].errorCodes;
    }
    return null;
  }, [assetErrors]);
  const bestPracticesText = useMemo(() => {
    const localeKey = FEATURE_BEST_PRACTICES_LOCALE_KEY[cleanedFeatureName];
    if (!localeKey) {
      return '';
    }
    return translate(localeKey);
  }, [cleanedFeatureName]);
  const {uiInteraction} = useUiInteractionEvent();
  const popperRef = useRef<Instance>(null);
  const uploadInteractionId = uuid();
  const processEventHandlers = useProcessEvent();
  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop: async (acceptedFiles) => {
      await onVideoUpload({
        files: acceptedFiles,
        feature,
        analyticsInteractionId: uploadInteractionId,
        analyticsLocation: feature,
        processEventHandlers,
        onFilesSelected: () => {
          contentLabActions.removeFeatureAssetErrors(feature);
        },
        onUploadError: (uniqueAssetId, file, _errorCodes) => {
          if (!_errorCodes) {
            return;
          }
          contentLabActions.addAssetErrors([
            {assetId: uniqueAssetId, errorCodes: _errorCodes, feature},
          ]);
        },
      });
    },
    accept: {
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'video/x-msvideo': ['.avi'],
      'video/webm': ['.webm'],
    },
    multiple: false,
    disabled: isLoading,
  });

  const handleImageLoad = () => {
    // Force popper to recalculate position after image loads
    if (popperRef.current) {
      popperRef.current.update();
    }
  };

  const bestPracticesElement = showBestPractices ? (
    <ClickAwayListener
      onClickAway={() => {
        setIsTooltipOpen(false);
      }}
    >
      <span style={{display: 'inline'}}>
        <Tooltip
          open={isTooltipOpen}
          title={
            <BestPracticesTooltip
              onImageLoad={handleImageLoad}
              onClose={() => setIsTooltipOpen(false)}
              feature={cleanedFeatureName}
            />
          }
          type="light"
          placement="right"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 10],
                  },
                },
              ],
            },
          }}
          PopperProps={{
            popperRef,
          }}
        >
          <span style={{display: 'inline'}}>
            <Body
              size="md"
              color={designSystemToken('semantic.fg.neutral')}
              onClick={(e) => {
                e.stopPropagation();
                setIsTooltipOpen(!isTooltipOpen);
              }}
              style={{display: 'inline'}}
            >
              {' '}
              {translate('See best practices')}
            </Body>
          </span>
        </Tooltip>
      </span>
    </ClickAwayListener>
  ) : null;

  return (
    <UploaderContainer
      isDragActive={isDragActive}
      minHeight={height}
      width={width}
      maxWidth={maxWidth}
    >
      <DropzoneArea
        {...getRootProps({
          onClick: () => {
            uiInteraction({
              item_name: 'browse_files',
              interaction_location: analyticsLocation,
              interaction_id: uploadInteractionId,
            });
          },
        })}
        className={`dropzone ${isDragActive ? 'active' : ''}`}
      >
        <input {...getInputProps()} />
        <UploaderContentStack direction="column">
          {errorCodes ? (
            <AssetErrorsNotificationBanner
              errorCodes={errorCodes}
              feature={feature}
            />
          ) : null}
          <TopStack>
            <Stack alignItems="center" gap="16px">
              <UploadIconContainer>
                <Icon
                  name="Actions-Upload"
                  size="medium"
                  appearance="primary"
                  style={{alignSelf: 'center'}}
                />
              </UploadIconContainer>
              <ActionButton
                appearance="neutral"
                mode="filled"
                size="medium"
                isLoading={isLoading}
              >
                {translate('Browse files')}
              </ActionButton>
              <Body
                size="md"
                color={designSystemToken('semantic.fg.secondary')}
              >
                {translate('Or drag and drop here')}
              </Body>
            </Stack>
          </TopStack>
          <BottomStack>
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                padding: '6px 16px',
                borderRadius: '12px',
                background: designSystemToken('semantic.bg.brand-secondary'),
              }}
            >
              <Body size="md" color={designSystemToken('semantic.bg.brand')}>
                {translate('max_duration_and_size_per_file', {
                  maxDuration: translate(uploadLimits.maxDurationDisplayKey),
                  maxSize: translate(uploadLimits.maxFileSizeDisplayKey),
                })}
              </Body>
            </Stack>
            {bestPracticesText ? (
              <BestPracticesContainer direction="column">
                <Body
                  component="div"
                  size="md"
                  color={designSystemToken('semantic.fg.secondary')}
                  style={{display: 'inline', whiteSpace: 'pre-wrap'}}
                >
                  {translate(bestPracticesText)}
                  {bestPracticesElement}
                </Body>
              </BestPracticesContainer>
            ) : null}
          </BottomStack>
        </UploaderContentStack>
      </DropzoneArea>
    </UploaderContainer>
  );
}

export default OnboardingFileUploader;
